import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaBell, FaBolt, FaCheckCircle, FaExclamationTriangle, FaRocket, FaSyncAlt, FaCheckDouble } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../hooks/useDarkMode';
 import { Helmet } from "react-helmet";
import { getNotificationsApi, markNotificationReadApi } from '../../apis/notifications.api';

const parseTimestamp = (ts) => {
  if (!ts) return null;
  
  // Handle Unix timestamp (number)
  if (typeof ts === 'number') {
    // Check if it's in seconds (Unix) or milliseconds
    const timestamp = ts < 10000000000 ? ts * 1000 : ts;
    return new Date(timestamp);
  }

  if (typeof ts === 'string') {
    // Check explicit patterns FIRST before trying generic ISO parsing
    
    // Handle "dd-mm-YYYY HH:MM:SS" format (e.g., "02-01-2026 11:47:35")
    const ddmmyyyyMatch = ts.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
    if (ddmmyyyyMatch) {
      const [, day, month, year, hour, minute, second] = ddmmyyyyMatch.map(v => parseInt(v, 10));
      return new Date(year, month - 1, day, hour, minute, second);
    }

    // Handle "YYYY-mm-dd HH:MM:SS" format
    const yyyymmddMatch = ts.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
    if (yyyymmddMatch) {
      const [, year, month, day, hour, minute, second] = yyyymmddMatch.map(v => parseInt(v, 10));
      return new Date(year, month - 1, day, hour, minute, second);
    }

    // Try ISO format as fallback (works for most standard formats)
    const isoDate = new Date(ts);
    if (!isNaN(isoDate.getTime()) && isoDate.getTime() > 0) {
      return isoDate;
    }
  }
  return null;
};

function timeAgo(timestamp) {
  if (!timestamp) return 'just now';
  const now = Date.now();
  const diff = Math.floor((now - timestamp) / 1000); // seconds
  
  if (diff < 0) return 'just now'; // Future dates
  if (diff < 60) return diff < 10 ? 'just now' : `${diff}s ago`;
  
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins}m ago`;
  
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

const Notification = () => {
  const { isDarkMode } = useDarkMode();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seenIdsRef = useRef(new Set());
  const initialLoadRef = useRef(true);
  const pollRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getNotificationsApi();
      const data = res?.data?.notifications ?? res?.data ?? [];
      const list = Array.isArray(data) ? data : [];

      // Detect new items for toast
      const newOnes = list.filter((n) => !seenIdsRef.current.has(n.id));
      if (!initialLoadRef.current && newOnes.length > 0) {
        newOnes.slice(0, 3).forEach((n) => {
          const msg = n.body?.length > 120 ? `${n.body.slice(0, 120)}…` : n.body;
          toast.info(msg || 'You have a new notification', {
            autoClose: 3000,
            toastId: `notif-${n.id}`,
            hideProgressBar: false,
          });
        });
      }

      // Update seen ids
      const nextSet = new Set(list.map((n) => n.id));
      seenIdsRef.current = nextSet;
      initialLoadRef.current = false;

      setNotifications(list);
    } catch (err) {
      // Handle token expiration (403 with Firebase token error)
      if (err?.response?.status === 403 && err?.response?.data?.detail?.includes('Firebase token')) {
        // console.log('[NOTIFICATION] Firebase token expired, attempting refresh...');
        try {
          const { refreshToken } = await import('../../utils/tokenManagement');
          const newToken = await refreshToken();
          if (newToken) {
            // console.log('[NOTIFICATION] Token refreshed successfully, retrying...');
            // Retry fetching notifications
            return fetchNotifications();
          }
        } catch (refreshErr) {
          // console.error('[NOTIFICATION] Token refresh failed:', refreshErr);
          setError('Session expired. Please refresh the page and sign in again.');
          return;
        }
      }
      
      setError(err?.response?.data?.error || err?.message || 'Unable to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Poll every 25 seconds for new notifications
    pollRef.current = setInterval(fetchNotifications, 25000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    try {
      await markNotificationReadApi(id);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to mark notification as read');
    }
  };

  const unreadCount = useMemo(() => notifications.filter((n) => !n.is_read).length, [notifications]);

  const getIcon = (title = '') => {
    const t = title.toLowerCase();
    if (t.includes('fail') || t.includes('error') || t.includes('expired')) return <FaExclamationTriangle className="text-orange-500" />;
    if (t.includes('payment') || t.includes('plan')) return <FaBolt className="text-blue-500" />;
    if (t.includes('keyword') || t.includes('scan') || t.includes('rank')) return <FaRocket className="text-green-500" />;
    return <FaBell className="text-yellow-500" />;
  };

  return (
    <>
   

<Helmet>

  {/* Title */}
  <title>Notifications | Seo Up Rank Dashboard</title>

  {/* Description */}
  <meta
    name="description"
    content="View your latest notifications, updates, alerts and activity related to your Seo Up Rank account and SEO tools."
  />

  {/* Private Page */}
  <meta name="robots" content="noindex, nofollow" />

  {/* Canonical */}
  <link rel="canonical" href="https://novarsis.ai/notifications" />

  {/* Open Graph */}
  <meta property="og:title" content="Seo Up Rank Notifications" />
  <meta
    property="og:description"
    content="Check important alerts and notifications from your Seo Up Rank dashboard."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://novarsis.ai/notifications" />
  <meta property="og:site_name" content="Seo Up Rank" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Seo Up Rank Notifications" />
  <meta
    name="twitter:description"
    content="Access your Seo Up Rank notifications and updates."
  />

  {/* Schema */}
  <script type="application/ld+json">
    {`
    {
      "@context":"https://schema.org",
      "@type":"WebPage",
      "name":"Notifications - Seo Up Rank",
      "url":"https://novarsis.ai/notifications",
      "description":"User notification page for alerts and updates in Seo Up Rank dashboard."
    }
    `}
  </script>

</Helmet>
   
    <div className={`min-h-[calc(100vh-120px)] px-4 sm:px-6 lg:px-8 py-6 sm:py-10 ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <div className="max-w-5xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className={`text-3xl sm:text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Notifications
              </h1>
              <p className={`text-sm sm:text-base mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Stay updated with your account activity
              </p>
            </div>
            <button
              onClick={fetchNotifications}
              className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition whitespace-nowrap ${
                isDarkMode
                  ? 'bg-[#008300] hover:bg-[#006600] text-white'
                  : 'bg-[#008300] hover:bg-[#006600] text-white'
              }`}
            >
              <FaSyncAlt className={`${loading ? 'animate-spin' : ''} text-lg`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mt-6">
            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
              <p className={`text-xs sm:text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total</p>
              <p className={`text-lg sm:text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {notifications.length}
              </p>
            </div>
            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-xs sm:text-sm font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>Unread</p>
              <p className={`text-lg sm:text-2xl font-bold mt-1 text-green-500`}>
                {unreadCount}
              </p>
            </div>
            <div className={`p-4 rounded-lg border col-span-2 sm:col-span-1 ${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
              <p className={`text-xs sm:text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Read</p>
              <p className={`text-lg sm:text-2xl font-bold mt-1 text-blue-500`}>
                {notifications.length - unreadCount}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-lg border flex items-center gap-3 ${
              isDarkMode ? 'bg-red-900/20 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
            }`}
          >
            <FaExclamationTriangle size={18} />
            <span className="text-sm sm:text-base">{error}</span>
          </motion.div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.1 }}
                className={`h-24 rounded-xl animate-pulse ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
              />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className={`text-center py-12 sm:py-16 rounded-xl border-2 border-dashed ${
              isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
            }`}
          >
            <FaCheckCircle className={`mx-auto mb-4 text-4xl sm:text-5xl ${isDarkMode ? 'text-green-500' : 'text-green-500'}`} />
            <h3 className={`text-lg sm:text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              All caught up!
            </h3>
            <p className={`text-sm sm:text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No notifications at the moment. New updates will appear here.
            </p>
          </motion.div>
        ) : (
          /* Notifications List */
          <div className="space-y-4">
            {notifications.map((n, idx) => {
              const createdDate = parseTimestamp(n.created_at);
              const isUnread = !n.is_read;

              return (
                <motion.div
                  key={n.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05, duration: 0.3 }}
                  className={`group p-4 sm:p-6 rounded-xl border transition-all duration-200 ${
                    isUnread
                      ? isDarkMode
                        ? 'bg-gray-900 border-[#008300]/50 shadow-lg shadow-green-500/10'
                        : 'bg-green-50 border-[#008300]/30 shadow-lg shadow-green-500/10'
                      : isDarkMode
                      ? 'bg-gray-900 border-gray-800 hover:border-gray-700'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex gap-4 items-start">
                    
                    {/* Icon */}
                    <div className={`shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-lg sm:text-xl ${
                      isUnread
                        ? isDarkMode ? 'bg-green-900/40' : 'bg-green-100'
                        : isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      {getIcon(n.title)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-bold text-base sm:text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {n.title}
                          </h3>
                          <p className={`text-sm sm:text-base leading-relaxed mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {n.body}
                          </p>
                        </div>
                        <span className={`text-xs sm:text-sm font-medium whitespace-nowrap ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {createdDate ? timeAgo(createdDate.getTime()) : 'just now'}
                        </span>
                      </div>

                      {/* Badge & Actions */}
                      <div className="flex items-center gap-3 flex-wrap mt-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isUnread
                            ? isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                            : isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {isUnread ? '🆕 New' : '✓ Read'}
                        </span>

                        {isUnread && (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleMarkRead(n.id)}
                            className={`flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3 py-1 rounded-lg transition ${
                              isDarkMode
                                ? 'text-green-300 hover:bg-green-900/30'
                                : 'text-green-700 hover:bg-green-100'
                            }`}
                          >
                            <FaCheckDouble size={12} />
                            <span className="hidden sm:inline">Mark as read</span>
                            <span className="sm:hidden">Read</span>
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        newestOnTop 
        closeOnClick 
        pauseOnHover={false} 
        draggable={false} 
        limit={3}
        theme={isDarkMode ? 'dark' : 'light'}
      />
    </div> </>
  );
};

export default Notification;